<template>
  <section>
    <div class="sum">
      <div class="sum__left">
        <h3 class="sum__name">{{ name }}</h3>
        <p class="sum__desc">{{ description }}</p>
        <span class="pill" :class="`pill--${pill.tone}`">{{ pill.label }}</span>
      </div>

      <div class="sum__right">
        <template v-if="o.trial">
          <div class="trial">
            <div class="trial__bar" role="progressbar" :aria-valuenow="Math.round(o.trial.progress * 100)" aria-valuemin="0" aria-valuemax="100">
              <div class="trial__fill" :style="{ width: `${Math.round(o.trial.progress * 100)}%` }"></div>
            </div>
            <div class="trial__days"><strong>{{ o.trial.daysLeft }}</strong> {{ o.trial.daysLeft === 1 ? 'día restante' : 'días restantes' }}</div>
            <div class="trial__end">Tu prueba termina el {{ formatLongDate(o.trial.endsAt) }}</div>
          </div>
        </template>
        <template v-else-if="o.plan">
          <div class="price">
            <span class="price__cur">$</span><span class="price__num">{{ formatMoney(o.plan.effectiveAmount) }}</span><span class="price__per">/mes</span>
          </div>
          <div v-if="o.plan.discountPercent" class="sum__discount">
            {{ o.plan.discountPercent }}% de descuento<span v-if="o.plan.discountUntil"> hasta el {{ formatShortDate(o.plan.discountUntil) }}</span>
            · precio de lista ${{ formatMoney(o.plan.monthlyAmount) }}
          </div>
          <div class="sum__since">Suscrito desde {{ formatShortDate(o.plan.subscribedSince) }}</div>
        </template>
        <div v-else class="sum__since">Sin cobro mensual asignado</div>
      </div>
    </div>

    <div v-if="usageRows.length" class="usage">
      <div v-for="u in usageRows" :key="u.label" class="usage__item">
        <div class="usage__head">
          <span>{{ u.label }}</span>
          <span class="usage__count">{{ u.max ? `${u.used} de ${u.max}` : `${u.used} · sin límite` }}</span>
        </div>
        <div v-if="u.max" class="usage__bar"><div class="usage__fill" :class="`usage__fill--${u.tone}`" :style="{ width: `${Math.min(100, (u.used / u.max) * 100)}%` }"></div></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { formatLongDate, formatShortDate, formatMoney } from './planFormat'

const props = defineProps({ overview: { type: Object, required: true } })
const o = computed(() => props.overview)

const name = computed(() => {
  if (o.value.plan) return `Plan ${o.value.plan.name}`
  return o.value.trial ? 'Prueba gratuita' : 'Sin plan asignado'
})
const description = computed(() => {
  if (o.value.plan) return o.value.plan.description || 'Todas las funciones incluidas.'
  return o.value.trial ? 'Acceso completo a Parkos mientras lo pruebas.' : 'Contacta a Parkos para asignar un plan a tu parque.'
})
const pill = computed(() => {
  if (o.value.trial) return { tone: 'blue', label: o.value.trial.expired ? 'Prueba terminada' : `Periodo de prueba · ${o.value.trial.daysLeft} ${o.value.trial.daysLeft === 1 ? 'día' : 'días'}` }
  if (o.value.status === 'OVERDUE') return { tone: 'red', label: 'Vencido' }
  if (o.value.status === 'NO_PLAN') return { tone: 'gray', label: 'Sin plan' }
  return { tone: 'green', label: 'Activo' }
})

const usageRows = computed(() => {
  const u = o.value.usage
  if (!u) return []
  const tone = (used, max) => (!max ? 'ok' : used >= max ? 'full' : used / max >= 0.8 ? 'warn' : 'ok')
  // Sin ningún límite (p. ej. durante la prueba) no hay nada que vigilar.
  if (!u.plots.max && !u.users.max) return []
  return [
    { label: 'Naves', ...u.plots, tone: tone(u.plots.used, u.plots.max) },
    { label: 'Usuarios', ...u.users, tone: tone(u.users.used, u.users.max) },
  ]
})
</script>

<style scoped>
.sum { display: flex; justify-content: space-between; align-items: center; gap: 24px; min-height: 120px; padding: 22px 26px; background: #f0f4f8; border: 1px solid #e2e8f0; border-radius: 8px; box-sizing: border-box; }
.sum__left { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; min-width: 0; }
.sum__name { margin: 0; font: 700 20px var(--font-display); color: #1e3a5f; }
.sum__desc { margin: 0; font: 400 12px var(--font-body); color: #64748b; }
.pill { margin-top: 4px; padding: 3px 10px; border-radius: 12px; font: 500 11px var(--font-body); }
.pill--green { background: #dcfce7; color: #166534; }
.pill--blue { background: #dbeafe; color: #1d4ed8; }
.pill--red { background: #fee2e2; color: #b91c1c; }
.pill--gray { background: #e2e8f0; color: #475569; }
.sum__right { text-align: right; flex-shrink: 0; }
.price { display: flex; align-items: baseline; justify-content: flex-end; gap: 3px; color: #1e3a5f; }
.price__cur { font: 400 16px var(--font-body); color: #64748b; }
.price__num { font: 700 28px var(--font-mono); }
.price__per { font: 400 14px var(--font-body); color: #64748b; }
.sum__since { margin-top: 4px; font: 400 11px var(--font-body); color: #64748b; }
.sum__discount { margin-top: 2px; font: 400 11px var(--font-body); color: #166534; }
.trial { width: 250px; text-align: left; }
.trial__bar { height: 8px; background: #dbe4ef; border-radius: 4px; overflow: hidden; }
.trial__fill { height: 100%; background: #3b82f6; border-radius: 4px; transition: width 0.4s ease; }
.trial__days { margin-top: 8px; font: 400 12px var(--font-body); color: #1e3a5f; }
.trial__days strong { font: 700 16px var(--font-mono); }
.trial__end { margin-top: 2px; font: 500 12px var(--font-body); color: #b45309; }

.usage { display: flex; gap: 24px; margin-top: 12px; }
.usage__item { flex: 1; min-width: 0; }
.usage__head { display: flex; justify-content: space-between; font: 400 11px var(--font-body); color: #64748b; margin-bottom: 4px; }
.usage__count { font-family: var(--font-mono); color: #1e3a5f; }
.usage__bar { height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden; }
.usage__fill { height: 100%; border-radius: 2px; }
.usage__fill--ok { background: #3b82f6; }
.usage__fill--warn { background: #f0a500; }
.usage__fill--full { background: #ef4444; }

@media (max-width: 640px) { .sum { flex-direction: column; align-items: flex-start; } .sum__right { text-align: left; } .price { justify-content: flex-start; } .trial { width: 100%; } .usage { flex-direction: column; gap: 10px; } }
</style>